import { secondMs } from "verdi-common/timeConstants";
import FocusContext from "@/services/map-management/focus-context";
import MapEntityBase, { OnMapEntityCreated } from "@/services/map-management/map-entities/map-entity-base";

const DEFAULT_TIMEOUT_MS = 10 * secondMs;

/**
 * Resolves when the map entity for a model exists.
 *
 * Presence in the registry is the readiness signal. `leafletElement` is not: only polygon entities
 * (AOI, zone, overlay) ever set it, so waiting on it would never resolve for a device marker.
 *
 * Necessary but no longer sufficient for the marker to draw: markers are paired from v2 DTOs, so the
 * marker appears once the create invalidation in `invalidate-on-legacy-writes.ts` refetches the list.
 */
export function waitForMapEntity(modelId: string, timeoutMs = DEFAULT_TIMEOUT_MS): Promise<MapEntityBase> {
    // The entity is created later than the model, when MapEntityBase is constructed and registers
    // itself in FocusContext.MapEntitesByModelID (see map-entity-base.js).
    const existing = FocusContext.MapEntitesByModelID[modelId];
    if (existing) {
        return Promise.resolve(existing);
    }

    return new Promise((resolve, reject) => {
        let listenerId: string | undefined;

        const finish = (mapEntity: MapEntityBase) => {
            clearTimeout(timeoutId);
            if (listenerId) {
                OnMapEntityCreated.removeListener(listenerId);
            }
            resolve(mapEntity);
        };

        // Fallback if the entity is created between the sync check above and listener registration,
        // or if OnMapEntityCreated fired before we subscribed.
        const timeoutId = setTimeout(() => {
            if (listenerId) {
                OnMapEntityCreated.removeListener(listenerId);
            }
            const lateEntity = FocusContext.MapEntitesByModelID[modelId];
            if (lateEntity) {
                resolve(lateEntity);
            } else {
                reject(new Error(`Timed out waiting for map entity for model ${modelId}`));
            }
        }, timeoutMs);

        // OnMapEntityCreated is triggered from the MapEntityBase constructor for every new entity.
        // Filter by model id so we only resolve when *this* model's entity exists.
        listenerId = OnMapEntityCreated.addListener((event: { target: MapEntityBase }) => {
            const mapEntity = event?.target as MapEntityBase | undefined;
            if (mapEntity?.id === modelId) {
                finish(mapEntity);
            }
        });
    });
}
